import { 
  useState, 
  useEffect 
} from 'react';
import { 
  Container,
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Grid,
  Button,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import LighthouseResults from './LighthouseResults';
import TechnologyDetection from './TechnologyDetection';

const PageSpeedResult = () => {
  const { hash } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [techData, setTechData] = useState(null);
  const [issueData, setIssueData] = useState(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [clipboardDenied, setClipboardDenied] = useState(false);

  // Update the handleCopy function
  const handleCopy = async () => {
    if (isSharing) return;
    
    const shareUrl = `${window.location.origin}/result/${hash}`;
    try {
      setIsSharing(true);
      setClipboardDenied(false);
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Website Analysis Results',
            text: 'Check out these website analysis results!',
            url: shareUrl
          });
        } catch (shareError) {
          if (shareError.name !== 'AbortError') {
            try {
              await navigator.clipboard.writeText(shareUrl);
              setShowCopySuccess(true);
            } catch (clipboardError) {
              console.warn('Clipboard access denied:', clipboardError);
              setClipboardDenied(true);
            }
          }
        }
      } else {
        try {
          await navigator.clipboard.writeText(shareUrl);
          setShowCopySuccess(true);
        } catch (clipboardError) {
          console.warn('Clipboard access denied:', clipboardError);
          setClipboardDenied(true);
        }
      }
    } catch (err) {
      console.error('Error sharing:', err);
    } finally {
      setIsSharing(false);
    }
  };

  // Update the handleFieldClick function
  const handleFieldClick = async (e) => {
    e.target.select(); // Select the entire text
    if (!isSharing) {
      try {
        setClipboardDenied(false);
        await navigator.clipboard.writeText(e.target.value);
        setShowCopySuccess(true);
      } catch (clipboardError) {
        console.warn('Clipboard access denied:', clipboardError);
        setClipboardDenied(true);
      }
    }
  };

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiPort = process.env.REACT_APP_API_PORT || '5002';
        const apiBaseUrl = process.env.REACT_APP_API_URL || `http://localhost:${apiPort}/api`;
        
        const response = await fetch(`${apiBaseUrl}/pagespeed/result/hash/${hash}`, {
          headers: {
            'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch analysis results');
        }

        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.message || 'Failed to load analysis results');
        }

        // Extract the analysis data
        const analysisData = data.data;
        const rawData1 = JSON.parse(analysisData.raw_data);
        const rawData2 = JSON.parse(rawData1.raw_data);
        
        console.log('Raw data:', rawData2);
        
        // Set the main result data
        setResult({
          rawData: rawData2,
          metrics: analysisData.raw_metrics,
          url: analysisData.url,
          mobile: analysisData.is_mobile
        });

        // Set technology data if available
        if (rawData2?.technologies?.results) {
          setTechData(rawData2.technologies.results);
        }

        // Set issue data if available
        if (rawData2?.issues) {
          setIssueData({
            brokenLinks: rawData2.issues.brokenLinks || [],
            scriptIssues: rawData2.issues.scriptIssues || [],
            imageIssues: rawData2.issues.imageIssues || []
          });
        }

      } catch (err) {
        console.error('Error fetching results:', err);
        setError(err.message || 'Failed to load analysis results');
      } finally {
        setLoading(false);
      }
    };

    if (hash) {
      fetchResults();
    }
  }, [hash]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh'
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography 
          variant="h6" 
          sx={{ mt: 2, color: 'text.secondary' }}
        >
          Loading Analysis Results...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Helmet>
        <title>Analysis Results | Website Analysis Suite</title>
        <meta name="description" content="View detailed website analysis results including performance metrics, technology stack, and optimization opportunities." />
      </Helmet>

      {error ? (
        <Alert 
          severity="error" 
          sx={{ 
            mt: 2, 
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Error Loading Results
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      ) : result && (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                mb: 1
              }}
            >
              Analysis Results
            </Typography>
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              component="div"
              sx={{ mb: 2 }}
            >
              Analysis for: <Box component="span" sx={{ color: 'primary.main', wordBreak: 'break-all' }}>{result.url}</Box>
            </Typography>

            {/* Share Section */}
            <Card sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="h6" gutterBottom>
                Share Analysis Results
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Share these analysis results with your team or clients using the link below:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={`${window.location.origin}/result/${hash}`}
                  onClick={handleFieldClick}
                  onFocus={handleFieldClick}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={clipboardDenied ? "Please manually copy the URL (Ctrl/Cmd + C)" : "Click anywhere in this field to copy"}>
                          <IconButton
                            edge="end"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy();
                            }}
                            disabled={isSharing}
                            sx={{ color: clipboardDenied ? 'warning.main' : 'primary.main' }}
                          >
                            {isSharing ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              navigator.share ? <ShareIcon /> : <ContentCopyIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    sx: {
                      cursor: 'text',
                      bgcolor: 'background.default',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }
                  }}
                  sx={{ 
                    bgcolor: 'background.default',
                    '& .MuiOutlinedInput-root': {
                      cursor: 'text',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleCopy}
                  disabled={isSharing}
                  startIcon={isSharing ? <CircularProgress size={20} /> : (navigator.share ? <ShareIcon /> : <ContentCopyIcon />)}
                  color={clipboardDenied ? "warning" : "primary"}
                >
                  {isSharing ? 'Sharing...' : (clipboardDenied ? 'Copy Manually' : (navigator.share ? 'Share' : 'Copy'))}
                </Button>
              </Box>
            </Card>
            
            <Divider />
          </Box>

          <Box mt={4}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <LighthouseResults 
                  data={result} 
                  isMobile={result.mobile}
                  techData={techData}
                  issueData={issueData}
                />
              </Grid>
            </Grid>
          </Box>

          {techData && (
            <Box mt={4}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                  <Typography 
                    variant="h4" 
                    component="h2" 
                    gutterBottom
                    sx={{ mb: 3, fontWeight: 600 }}
                  >
                    Technology Stack
                  </Typography>
                  <Divider sx={{ mb: 4 }} />
                  
                  <TechnologyDetection data={techData} />
                </CardContent>
              </Card>
            </Box>
          )}

          <Snackbar
            open={showCopySuccess || clipboardDenied}
            autoHideDuration={5000}
            onClose={() => {
              setShowCopySuccess(false);
              setClipboardDenied(false);
            }}
            message={clipboardDenied 
              ? "Clipboard access denied. Please use Ctrl/Cmd + C to copy manually." 
              : "Share link copied to clipboard!"
            }
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{
              '& .MuiSnackbarContent-root': {
                bgcolor: clipboardDenied ? 'warning.dark' : 'success.main'
              }
            }}
          />
        </>
      )}
    </Container>
  );
};

export default PageSpeedResult;